import React, { useState, useRef } from 'react';
import { bulkTaskApi } from '../api';
import { Btn, PageHeader, Alert } from '../components/ui';

export default function BulkTaskUpload() {
  const [file,      setFile]      = useState(null);
  const [result,    setResult]    = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await bulkTaskApi.downloadTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = 'tracern-task-upload-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download template'); }
    finally  { setDownloading(false); }
  };

  const handleUpload = async () => {
    if (!file) { setError('Select a file first'); return; }
    setUploading(true); setError(''); setResult(null);
    try {
      const res = await bulkTaskApi.upload(file);
      setResult(res.data?.data);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) { setError(e.response?.data?.message || 'Upload failed'); }
    finally     { setUploading(false); }
  };

  const successRows = result?.results?.filter(r => r.success)  || [];
  const failedRows  = result?.results?.filter(r => !r.success) || [];

  return (
    <div style={{ padding: '20px 16px', maxWidth: '720px', margin: '0 auto' }}>
      <PageHeader
        title="Bulk Task Upload"
        subtitle="Download the template, fill in your tasks, then upload."
      />

      {error && <Alert type="error" message={error} />}

      {/* Step 1: Download template */}
      <div style={S.stepCard}>
        <div style={S.stepNum}>1</div>
        <div style={{ flex: 1 }}>
          <p style={S.stepTitle}>Download the template</p>
          <p style={S.stepSub}>
            Get the Excel template with the correct columns and sample data.
            Fill in your tasks — max 200 rows per upload.
          </p>
          <div style={{ marginTop: '12px' }}>
            <Btn size="sm" onClick={handleDownload} loading={downloading} variant="ghost">
              ⬇ Download Template (.xlsx)
            </Btn>
          </div>
        </div>
      </div>

      {/* Step 2: Fill & upload */}
      <div style={S.stepCard}>
        <div style={S.stepNum}>2</div>
        <div style={{ flex: 1 }}>
          <p style={S.stepTitle}>Upload your filled file</p>
          <p style={S.stepSub}>Only .xlsx and .xls files accepted. Max 200 rows.</p>

          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) setFile(f);
            }}
            style={{
              marginTop: '12px',
              border: `2px dashed ${file ? '#10B981' : '#D1D5DB'}`,
              borderRadius: '12px',
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: file ? '#F0FDF4' : '#F9FAFB',
              transition: 'all 0.15s',
            }}
          >
            <p style={{ fontSize: '28px', marginBottom: '6px' }}>
              {file ? '📄' : '📂'}
            </p>
            <p style={{ fontSize: '14px', fontWeight: 600, color: file ? '#065F46' : '#374151' }}>
              {file ? file.name : 'Click to select or drag and drop your file here'}
            </p>
            {file && (
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
            <input ref={inputRef} type="file" accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
          </div>

          {file && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Btn onClick={handleUpload} loading={uploading}>
                Upload &amp; Create Tasks →
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ''; }}>
                Clear
              </Btn>
            </div>
          )}
        </div>
      </div>

      {/* Step 3: Results */}
      {result && (
        <div style={S.stepCard}>
          <div style={{ ...S.stepNum, background: result.failedCount === 0 ? '#10B981' : '#F59E0B' }}>3</div>
          <div style={{ flex: 1 }}>
            <p style={S.stepTitle}>Upload Results</p>

            {/* Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', margin: '12px 0' }}>
              {[
                { label: 'Total Rows',  value: result.totalRows,    color: '#374151' },
                { label: 'Created',     value: result.successCount, color: '#15803D' },
                { label: 'Failed',      value: result.failedCount,  color: result.failedCount > 0 ? '#DC2626' : '#9CA3AF' },
              ].map(s => (
                <div key={s.label} style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '24px', fontWeight: 800, color: s.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{s.value}</p>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Failed rows */}
            {failedRows.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#DC2626', marginBottom: '8px' }}>
                  Failed rows — fix these and re-upload:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '280px', overflowY: 'auto' }}>
                  {failedRows.map(r => (
                    <div key={r.rowNumber} style={{
                      background: '#FEF2F2', border: '1px solid #FECACA',
                      borderRadius: '8px', padding: '10px 12px',
                      display: 'flex', gap: '10px', alignItems: 'flex-start',
                    }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, background: '#DC2626', color: '#fff', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>
                        Row {r.rowNumber}
                      </span>
                      <div>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{r.taskTitle}</p>
                        <p style={{ fontSize: '12px', color: '#DC2626', marginTop: '2px' }}>{r.error}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success list (collapsed) */}
            {successRows.length > 0 && (
              <details style={{ marginTop: '12px' }}>
                <summary style={{ fontSize: '13px', fontWeight: 600, color: '#15803D', cursor: 'pointer' }}>
                  ✓ {successRows.length} tasks created successfully
                </summary>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {successRows.map(r => (
                    <div key={r.rowNumber} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', color: '#374151', padding: '6px 10px', background: '#F0FDF4', borderRadius: '6px' }}>
                      <span style={{ color: '#10B981', fontWeight: 700 }}>✓</span>
                      <span>Row {r.rowNumber}</span>
                      <span style={{ color: '#6B7280' }}>—</span>
                      <span>{r.taskTitle}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '14px', padding: '18px', marginTop: '8px' }}>
        <p style={{ fontWeight: 700, fontSize: '14px', color: '#374151', marginBottom: '10px' }}>Rules &amp; Notes</p>
        <ul style={{ paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            'Title, Priority, and Deadline are required for every row.',
            'Priority must be exactly: LOW, MEDIUM, HIGH, or CRITICAL (uppercase).',
            'Deadline format: dd/MM/yyyy  e.g. 25/12/2025.',
            'Department Name must match an existing department exactly.',
            'Assign To must be a valid Employee ID in your company.',
            'Leave Assign To blank to send the task to the shared pool.',
            'As an Employee, all tasks are assigned to yourself.',
            'Maximum 200 rows per upload.',
          ].map((r, i) => (
            <li key={i} style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.55 }}>{r}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const S = {
  stepCard: {
    display: 'flex', gap: '14px', alignItems: 'flex-start',
    background: '#fff', border: '1px solid #E5E7EB', borderRadius: '14px',
    padding: '18px', marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,.04)',
  },
  stepNum: {
    width: '28px', height: '28px', borderRadius: '50%',
    background: '#111827', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: 800, flexShrink: 0, marginTop: '1px',
  },
  stepTitle: { fontWeight: 700, fontSize: '15px', color: '#111827', marginBottom: '4px' },
  stepSub:   { fontSize: '13px', color: '#6B7280', lineHeight: 1.6 },
};
